import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import {
  LayoutDashboard, Building2, LogOut, Menu, X, Shield, Mail, Sparkles, Lightbulb, Package, Ticket, TrendingDown
} from "lucide-react";
import ChangeEmailDialog from "./ChangeEmailDialog";

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  const navItems = [
    { to: "/admin", icon: <LayoutDashboard className="w-5 h-5" />, label: "Overview", end: true },
    { to: "/admin/tenants", icon: <Building2 className="w-5 h-5" />, label: "Tenants" },
    { to: "/admin/plans", icon: <Package className="w-5 h-5" />, label: "Plans & Pricing" },
    { to: "/admin/discounts", icon: <Ticket className="w-5 h-5" />, label: "Discounts" },
    { to: "/admin/roadmap", icon: <Sparkles className="w-5 h-5" />, label: "Roadmap" },
    { to: "/admin/feature-requests", icon: <Lightbulb className="w-5 h-5" />, label: "Customer Ideas" },
    { to: "/admin/churn", icon: <TrendingDown className="w-5 h-5" />, label: "Churn Insights" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Mobile menu */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#0A192F] text-white rounded-lg"
        data-testid="admin-mobile-menu"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-[#0A192F] border-r border-[#112240] flex flex-col transition-transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-5 border-b border-[#112240]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#0A192F]" />
            </div>
            <div>
              <p className="text-white font-bold tracking-tight">Nirvanta Admin</p>
              <p className="text-[10px] text-amber-300/80 uppercase tracking-wider">Super Admin Console</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive ? "bg-amber-500 text-[#0A192F] font-semibold" : "text-slate-300 hover:bg-[#112240] hover:text-white"
                }`
              }
            >
              {item.icon}
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[#112240] space-y-1">
          <button
            onClick={() => setShowChangeEmail(true)}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-[#112240] hover:text-white transition-colors text-sm"
            data-testid="admin-change-email-btn"
          >
            <Mail className="w-4 h-4" />
            <span>Change email</span>
          </button>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-red-500/20 hover:text-red-300 transition-colors text-sm"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
          <div className="px-4 pt-3 text-xs text-slate-500 truncate" data-testid="admin-user-email">
            {user?.email}
          </div>
        </div>
      </aside>

      <ChangeEmailDialog open={showChangeEmail} onOpenChange={setShowChangeEmail} />

      <main className="flex-1 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
